import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpRight,
  ExternalLink,
  Calendar,
  Plus,
  Minus,
} from "lucide-react";
import { APPLICATION_LINK_PROPS } from "../config/applicationForm";
import { mailtoLink } from "../config/contacts";
import RelatedServices from "./RelatedServices";

/**
 * Shared editorial template for a Consulting service landing page.
 * Preserves the design language established by YouthMentorshipGuidance.jsx.
 *
 * Sections:
 *  1. Hero (cinematic image + eyebrow + title + subtitle + primary/secondary CTAs)
 *  2. Overview
 *  3. Who It Serves
 *  4. What Participants Receive
 *  5. Benefits (icon pillar cards)
 *  6. Frequently Asked Questions
 *  7. Final CTA
 *  8. Booking
 */

const Eyebrow = ({ children }) => (
  <div className="text-[10.5px] tracking-[0.34em] uppercase text-gold">
    {children}
  </div>
);

const SectionTitle = ({ children, className = "" }) => (
  <h2
    className={`font-serif text-white text-[26px] sm:text-[32px] lg:text-[38px] leading-[1.08] tracking-[-0.012em] ${className}`}
  >
    {children}
  </h2>
);

const SectionWrap = ({ id, children, className = "" }) => (
  <section id={id} className={`relative border-b border-white/5 ${className}`}>
    <div className="max-w-[1180px] mx-auto px-6 lg:px-12 py-16 lg:py-20">
      {children}
    </div>
  </section>
);

const PillarCard = ({ Icon, title, body }) => (
  <div className="group relative border border-white/10 bg-ink-soft/40 hover:border-azure/40 transition-colors duration-500 p-6 lg:p-7 h-full flex flex-col">
    <span
      aria-hidden="true"
      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
      style={{
        background:
          "radial-gradient(ellipse 80% 100% at 0% 0%, rgba(74,143,229,0.10), transparent 70%)",
      }}
    />
    <div className="relative">
      <div className="w-10 h-10 rounded-full border border-azure-soft/40 flex items-center justify-center text-azure-soft">
        <Icon className="w-4 h-4" strokeWidth={1.6} />
      </div>
      <h3 className="mt-6 font-serif text-white text-[17px] lg:text-[18px] leading-[1.25] tracking-[-0.01em] group-hover:text-azure-soft transition-colors duration-500">
        {title}
      </h3>
      <p className="mt-3 text-white/65 text-[14px] leading-[1.8]">{body}</p>
    </div>
  </div>
);

const FaqItem = ({ q, a, isOpen, onToggle, testId }) => (
  <div className="border-b border-white/10">
    <button
      type="button"
      onClick={onToggle}
      data-testid={testId}
      className="w-full flex items-center justify-between text-left py-5 group"
    >
      <span className="font-serif text-white text-[17px] lg:text-[19px] tracking-[-0.005em] group-hover:text-azure-soft transition-colors">
        {q}
      </span>
      <span className="ml-6 shrink-0 w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/60 group-hover:text-gold group-hover:border-gold transition-colors">
        {isOpen ? (
          <Minus className="w-4 h-4" strokeWidth={1.6} />
        ) : (
          <Plus className="w-4 h-4" strokeWidth={1.6} />
        )}
      </span>
    </button>
    {isOpen && (
      <div className="pb-6 pr-12 text-white/65 text-[14.5px] leading-[1.85]">{a}</div>
    )}
  </div>
);

/**
 * Props:
 *  - testId, slug (used for data-testids)
 *  - eyebrow, titleLead, titleItalic  (hero headline)
 *  - subtitle
 *  - heroImage, heroImageAlt
 *  - overviewEyebrow, overviewTitleLead, overviewTitleItalic, overviewBody[]
 *  - whoItServes: { title, description }[]
 *  - receive: string[]
 *  - benefits: { Icon, title, body }[]
 *  - faqs: { q, a }[]
 *  - finalHeadlineLead, finalHeadlineItalic, finalBody
 *  - bookingSubject (mailto subject for the booking CTA)
 *  - primaryCta: { label, kind: "form" | "mailto", subject? }
 */
const ServicePageTemplate = ({
  testId,
  slug,
  eyebrow,
  titleLead,
  titleItalic,
  subtitle,
  heroImage,
  heroImageAlt,
  overviewEyebrow,
  overviewTitleLead,
  overviewTitleItalic,
  overviewBody,
  whoItServes,
  receive,
  benefits,
  faqs,
  finalHeadlineLead,
  finalHeadlineItalic,
  finalBody,
  primaryCta,
  bookingSubject,
  relatedSlug,
}) => {
  const [openFaq, setOpenFaq] = useState(0);

  const primaryCtaProps =
    primaryCta.kind === "form"
      ? APPLICATION_LINK_PROPS
      : { href: mailtoLink("booking", primaryCta.subject) };

  const discoveryMailto = mailtoLink("booking", `Discovery Call — ${titleLead}`);
  const bookingMailto = mailtoLink("booking", bookingSubject);

  return (
    <main data-testid={testId} className="bg-ink min-h-screen">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt={heroImageAlt}
            className="w-full h-full object-cover opacity-55"
          />
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(90deg, rgba(8,13,28,0.94) 0%, rgba(8,13,28,0.78) 45%, rgba(8,13,28,0.55) 100%), radial-gradient(ellipse 55% 60% at 80% 20%, rgba(74,143,229,0.16), transparent 65%)",
            }}
          />
        </div>

        <div className="relative max-w-[1180px] mx-auto px-6 lg:px-12 pt-28 lg:pt-32 pb-20 lg:pb-24">
          <Link
            to="/consulting"
            data-testid={`${slug}-back-link`}
            className="group inline-flex items-center gap-3 text-[10.5px] tracking-[0.34em] uppercase text-white/55 hover:text-azure-soft transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5 transition-transform duration-500 group-hover:-translate-x-1" />
            Back to Formation &amp; Initiatives
          </Link>

          <div className="mt-8 flex items-center gap-4">
            <span className="text-[11px] tracking-[0.34em] uppercase text-azure-soft">
              {eyebrow}
            </span>
            <span className="h-px w-12 bg-azure-soft/50" />
          </div>

          <h1 className="mt-7 font-serif text-white text-[34px] sm:text-[48px] lg:text-[64px] leading-[1.02] tracking-[-0.02em] max-w-3xl">
            {titleLead}{" "}
            {titleItalic && (
              <span className="italic text-azure-soft">{titleItalic}</span>
            )}
            .
          </h1>

          <div className="mt-8 h-px w-20 bg-gold" />

          <p className="mt-8 text-white/75 text-[16px] lg:text-[17px] leading-[1.9] max-w-2xl">
            {subtitle}
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <a
              {...primaryCtaProps}
              target={primaryCta.kind === "form" ? "_blank" : undefined}
              rel={primaryCta.kind === "form" ? "noopener noreferrer" : undefined}
              data-testid={`${slug}-hero-primary`}
              className="group inline-flex items-center justify-center gap-3 bg-azure hover:bg-[#5C9BEC] text-white px-7 py-3.5 text-[11px] tracking-[0.32em] uppercase font-medium transition-all"
            >
              {primaryCta.label}
              {primaryCta.kind === "form" ? (
                <ExternalLink className="w-3.5 h-3.5" />
              ) : (
                <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.8} />
              )}
            </a>
            <a
              href={discoveryMailto}
              data-testid={`${slug}-hero-discovery`}
              className="group inline-flex items-center justify-center gap-3 border border-white/40 hover:border-gold hover:text-gold text-white px-7 py-3.5 text-[11px] tracking-[0.32em] uppercase font-medium transition-all"
            >
              Schedule A Discovery Call
              <Calendar className="w-3.5 h-3.5" strokeWidth={1.8} />
            </a>
          </div>
        </div>
      </section>

      {/* 2 — OVERVIEW */}
      <SectionWrap id="overview">
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-10 lg:gap-16 items-start">
          <div>
            <Eyebrow>{overviewEyebrow}</Eyebrow>
            <SectionTitle className="mt-5">
              {overviewTitleLead}{" "}
              {overviewTitleItalic && (
                <span className="italic text-azure-soft">
                  {overviewTitleItalic}
                </span>
              )}
              .
            </SectionTitle>
            <div className="mt-7 h-px w-12 bg-gold" />
          </div>
          <div className="space-y-6 text-white/75 text-[15.5px] leading-[1.9]">
            {overviewBody.map((p, idx) => (
              <p key={idx}>{p}</p>
            ))}
          </div>
        </div>
      </SectionWrap>

      {/* 3 — WHO IT SERVES */}
      <SectionWrap id="who" className="bg-ink-deep/40">
        <div className="text-center max-w-2xl mx-auto">
          <Eyebrow>Who It Serves</Eyebrow>
          <SectionTitle className="mt-5">
            Designed for{" "}
            <span className="italic text-azure-soft">specific stages</span>.
          </SectionTitle>
          <div className="mt-7 h-px w-12 bg-gold mx-auto" />
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {whoItServes.map((w) => (
            <div
              key={w.title}
              className="border border-white/10 bg-ink-soft/30 p-6 lg:p-7 h-full"
            >
              <div className="text-[10.5px] tracking-[0.34em] uppercase text-gold">
                {w.title}
              </div>
              <div className="mt-4 h-px w-8 bg-azure-soft/50" />
              <p className="mt-5 text-white/65 text-[14px] leading-[1.85]">
                {w.description}
              </p>
            </div>
          ))}
        </div>
      </SectionWrap>

      {/* 4 — WHAT PARTICIPANTS RECEIVE */}
      <SectionWrap id="receive">
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-10 lg:gap-16 items-start">
          <div>
            <Eyebrow>What You Receive</Eyebrow>
            <SectionTitle className="mt-5">
              A structured{" "}
              <span className="italic text-azure-soft">program</span>.
            </SectionTitle>
            <div className="mt-7 h-px w-12 bg-gold" />
            <p className="mt-7 text-white/70 text-[14.5px] leading-[1.9] max-w-md">
              Every engagement includes a considered set of deliverables,
              tailored to the participant&apos;s goals and stage.
            </p>
          </div>
          <ul className="space-y-3">
            {receive.map((item, idx) => (
              <li
                key={idx}
                className="flex items-start gap-4 border border-white/10 bg-ink-soft/30 px-5 py-4"
              >
                <span className="mt-2 w-1.5 h-1.5 bg-gold shrink-0" />
                <span className="text-white/80 text-[14.5px] leading-[1.85] tracking-[-0.005em]">
                  {item}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </SectionWrap>

      {/* 5 — BENEFITS */}
      <SectionWrap id="benefits" className="bg-ink-deep/40">
        <div className="text-center max-w-2xl mx-auto">
          <Eyebrow>Why It Matters</Eyebrow>
          <SectionTitle className="mt-5">
            The lasting{" "}
            <span className="italic text-azure-soft">benefits</span>.
          </SectionTitle>
          <div className="mt-7 h-px w-12 bg-gold mx-auto" />
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {benefits.map((b) => (
            <PillarCard key={b.title} {...b} />
          ))}
        </div>
      </SectionWrap>

      {/* 6 — FAQ */}
      <SectionWrap id="faq">
        <div className="grid lg:grid-cols-[0.85fr_1.6fr] gap-10 lg:gap-16">
          <div>
            <Eyebrow>Frequently Asked</Eyebrow>
            <SectionTitle className="mt-5">
              Common{" "}
              <span className="italic text-azure-soft">questions</span>.
            </SectionTitle>
            <div className="mt-7 h-px w-12 bg-gold" />
          </div>
          <div>
            {faqs.map((f, idx) => (
              <FaqItem
                key={f.q}
                q={f.q}
                a={f.a}
                isOpen={openFaq === idx}
                onToggle={() => setOpenFaq(openFaq === idx ? -1 : idx)}
                testId={`${slug}-faq-${idx}`}
              />
            ))}
          </div>
        </div>
      </SectionWrap>

      {/* 7 — FINAL CTA */}
      <section id="final-cta" className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt=""
            className="w-full h-full object-cover opacity-30"
          />
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(8,13,28,0.85) 0%, rgba(8,13,28,0.75) 60%, rgba(8,13,28,0.95) 100%), radial-gradient(ellipse 60% 50% at 50% 0%, rgba(74,143,229,0.14), transparent 65%)",
            }}
          />
        </div>
        <div className="relative max-w-[1100px] mx-auto px-6 lg:px-12 py-24 lg:py-28 text-center">
          <Eyebrow>The Next Step</Eyebrow>
          <h2 className="mt-5 font-serif text-white text-[30px] sm:text-[42px] lg:text-[54px] leading-[1.05] tracking-[-0.015em]">
            {finalHeadlineLead}{" "}
            {finalHeadlineItalic && (
              <span className="italic text-azure-soft">
                {finalHeadlineItalic}
              </span>
            )}
            .
          </h2>
          <div className="mt-8 h-px w-16 bg-gold mx-auto" />
          <p className="mt-8 text-white/75 text-[15.5px] lg:text-[16.5px] leading-[1.9] max-w-2xl mx-auto">
            {finalBody}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              {...primaryCtaProps}
              target={primaryCta.kind === "form" ? "_blank" : undefined}
              rel={primaryCta.kind === "form" ? "noopener noreferrer" : undefined}
              data-testid={`${slug}-final-primary`}
              className="group inline-flex items-center justify-center gap-3 bg-azure hover:bg-[#5C9BEC] text-white px-8 py-4 text-[11px] tracking-[0.32em] uppercase font-medium transition-all"
            >
              {primaryCta.label}
              {primaryCta.kind === "form" ? (
                <ExternalLink className="w-3.5 h-3.5" />
              ) : (
                <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.8} />
              )}
            </a>
            <a
              href={discoveryMailto}
              data-testid={`${slug}-final-discovery`}
              className="group inline-flex items-center justify-center gap-3 border border-white/40 hover:border-gold hover:text-gold text-white px-8 py-4 text-[11px] tracking-[0.32em] uppercase font-medium transition-all"
            >
              Schedule A Discovery Call
              <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-400 group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={1.8} />
            </a>
          </div>
        </div>
      </section>

      {/* 8 — BOOKING */}
      <section id="booking">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-16 lg:py-20">
          <div className="border border-gold/30 bg-ink-soft/40">
            <div className="relative overflow-hidden">
              <span
                aria-hidden="true"
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(ellipse 60% 100% at 100% 0%, rgba(201,169,97,0.08), transparent 65%)",
                }}
              />
              <div className="relative p-8 sm:p-10 lg:p-12 grid lg:grid-cols-[1fr_auto] gap-8 items-center">
                <div>
                  <Eyebrow>Booking</Eyebrow>
                  <h3 className="mt-5 font-serif text-white text-[22px] sm:text-[26px] lg:text-[30px] leading-[1.15] tracking-[-0.01em]">
                    Ready to begin?{" "}
                    <span className="italic text-azure-soft">
                      Reserve your session
                    </span>
                    .
                  </h3>
                  <p className="mt-4 text-white/65 text-[14.5px] leading-[1.85] max-w-xl">
                    Sessions are limited each cohort. Send a brief note and the
                    team will confirm availability and next steps.
                  </p>
                  <div className="mt-5 text-white/55 text-[12.5px] font-mono">
                    booking@theiyare.com
                  </div>
                </div>
                <a
                  href={bookingMailto}
                  data-testid={`${slug}-booking-cta`}
                  className="group inline-flex items-center justify-center gap-3 bg-gold hover:bg-[#D4B872] text-ink-deep px-8 py-4 text-[11px] tracking-[0.32em] uppercase font-semibold transition-all whitespace-nowrap"
                >
                  Book a Session
                  <ArrowUpRight
                    className="w-3.5 h-3.5 transition-transform duration-400 group-hover:translate-x-1 group-hover:-translate-y-1"
                    strokeWidth={1.8}
                  />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* 9 — RELATED SERVICES */}
      <RelatedServices currentSlug={relatedSlug} />
    </main>
  );
};

export default ServicePageTemplate;
